import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Check, RotateCcw, Sparkles } from "lucide-react";

/**
 * Reusable "which optional plan fits me?" widget.
 *
 * Props:
 * - testid:   string prefix used for data-testid attributes.
 * - options:  exactly 2 items → [leftCard, rightCard]
 *             { id, title, icon, tag, fitLines[], ctaLabel, ctaUrl, testid }
 * - quiz:     [{ id, question, answers: [{ label, scoreFor: 'left'|'right' }] }]
 * - fallback: optional { label, url, testid } — small "not sure?" link
 * - onResult: optional (option | { id: "tie" } | null) => void, fired when the quiz completes/resets
 * - onCtaClick: optional (option) => void, fired when a card CTA is clicked
 *
 * Behaviour:
 * - User taps through 2–3 questions; each answer adds a point to one side.
 * - Once every question is answered, the winning card gets a "Recommended for you"
 *   ribbon + brand-primary ring and the losing card fades slightly.
 * - Ties leave both cards neutral and the reset button appears.
 * - Everything is progressive-enhancement — cards stay clickable at every step.
 */
export const IndemnityComparison = ({ testid, options, quiz, fallback, onResult, onCtaClick }) => {
  const [answers, setAnswers] = useState({}); // { [quizId]: 'left'|'right' }

  const answered = Object.keys(answers).length;
  const total = quiz.length;
  const isComplete = answered === total;

  const scores = useMemo(() => {
    const s = { left: 0, right: 0 };
    Object.values(answers).forEach((side) => {
      if (side === "left" || side === "right") s[side] += 1;
    });
    return s;
  }, [answers]);

  const winner = useMemo(() => {
    if (!isComplete) return null;
    if (scores.left === scores.right) return null;
    return scores.left > scores.right ? "left" : "right";
  }, [scores, isComplete]);

  const sideForIndex = (i) => (i === 0 ? "left" : "right");

  useEffect(() => {
    if (!onResult) return;
    if (!isComplete) onResult(null);
    else if (winner) onResult(options[winner === "left" ? 0 : 1]);
    else onResult({ id: "tie", title: "Even match" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete, winner]);

  return (
    <div data-testid={testid}>
      {/* Mini quiz */}
      <div
        className="rounded-2xl border border-slate-200 bg-white/70 p-5 sm:p-6"
        data-testid={`${testid}-quiz`}
        aria-label="Which optional plan typically fits me"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[var(--bg-secondary)] text-[var(--brand-primary)]">
              <Sparkles className="w-4 h-4" />
            </span>
            <p className="text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold">
              Which fits me?
            </p>
          </div>
          <p className="text-[11px] text-slate-500 tabular-nums" data-testid={`${testid}-progress`}>
            {answered} / {total}
          </p>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4">
          {quiz.map((q, qi) => (
            <div key={q.id} data-testid={`${testid}-q-${q.id}`}>
              <p className="text-sm text-[var(--ink)] font-medium">
                <span className="text-slate-400 mr-2">{qi + 1}.</span>
                {q.question}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {q.answers.map((a, ai) => {
                  const active = answers[q.id] === a.scoreFor;
                  return (
                    <button
                      key={ai}
                      type="button"
                      data-testid={`${testid}-q-${q.id}-a-${ai}`}
                      onClick={() =>
                        setAnswers((prev) => ({ ...prev, [q.id]: a.scoreFor }))
                      }
                      className={`rounded-full px-4 h-9 text-xs font-medium transition-colors border ${
                        active
                          ? "bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white"
                          : "bg-white border-slate-200 text-slate-600 hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
                      }`}
                      aria-pressed={active}
                    >
                      {a.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {(answered > 0 || isComplete) && (
          <div className="mt-5 flex items-center gap-3 text-xs text-slate-500">
            <button
              type="button"
              onClick={() => setAnswers({})}
              data-testid={`${testid}-reset`}
              className="inline-flex items-center gap-1.5 text-slate-500 hover:text-[var(--brand-primary)] transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
            {isComplete && winner === null && (
              <span data-testid={`${testid}-tie`}>
                Even match — either option can fit. Talk it through with us.
              </span>
            )}
            {isComplete && winner && (
              <span data-testid={`${testid}-winner`}>
                Based on your answers, we highlighted the option that typically fits.
              </span>
            )}
          </div>
        )}
        <p className="mt-4 text-[10px] leading-relaxed text-slate-500">
          For guidance only — not personalized insurance advice.
        </p>
      </div>

      {/* Comparison cards */}
      <div
        className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4"
        data-testid={`${testid}-cards`}
      >
        {options.map((opt, i) => {
          const side = sideForIndex(i);
          const isWinner = winner === side;
          const isLoser = winner && winner !== side;
          const IconEl = opt.icon;
          return (
            <article
              key={opt.id}
              data-testid={`${opt.testid}-card`}
              aria-current={isWinner ? "true" : undefined}
              className={`relative rounded-2xl border bg-white p-6 flex flex-col transition-all duration-300 ${
                isWinner
                  ? "border-[var(--brand-primary)] ring-2 ring-[var(--brand-primary)]/25 shadow-[0_18px_44px_rgba(11,58,91,0.10)]"
                  : "border-slate-200"
              } ${isLoser ? "opacity-70" : ""}`}
            >
              {isWinner && (
                <span
                  data-testid={`${opt.testid}-badge`}
                  className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-[var(--brand-primary)] text-white text-[10px] font-semibold uppercase tracking-[0.14em] px-3 py-1"
                >
                  <Sparkles className="w-3 h-3" /> Recommended for you
                </span>
              )}
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[var(--bg-secondary)] text-[var(--brand-primary)]">
                  <IconEl className="w-5 h-5" />
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  {opt.tag}
                </span>
              </div>
              <h3 className="mt-4 font-display text-lg text-[var(--ink)] leading-snug">
                {opt.title}
              </h3>
              <p className="mt-3 text-[11px] uppercase tracking-[0.14em] text-slate-500">
                Typically fits if…
              </p>
              <ul className="mt-2 space-y-2 flex-1">
                {opt.fitLines.map((line, li) => (
                  <li
                    key={li}
                    className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed"
                  >
                    <Check className="w-4 h-4 mt-0.5 flex-none text-[var(--brand-primary)]" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <a
                href={opt.ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${opt.testid}-cta`}
                onClick={() => onCtaClick?.(opt)}
                className={`mt-6 inline-flex items-center justify-center rounded-full text-sm font-medium px-5 h-11 transition-colors border ${
                  isWinner
                    ? "bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-dark)]"
                    : "bg-white border-slate-300 text-[var(--ink)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
                }`}
              >
                {opt.ctaLabel} <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </article>
          );
        })}
      </div>

      {fallback && (
        <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-2 text-sm text-slate-600">
          <span>Not sure which one fits?</span>
          <a
            href={fallback.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={fallback.testid}
            className="inline-flex self-start items-center text-[var(--brand-primary)] hover:underline font-medium"
          >
            {fallback.label} <ArrowRight className="w-4 h-4 ml-1.5" />
          </a>
        </div>
      )}
    </div>
  );
};

export default IndemnityComparison;
